/**
 * Pearls, paths and loops of a masyu. Pure functions: the TypeScript port of
 * `backend/apps/puzzletypes/masyu/loop.py`, kept at parity by the shared fixtures.
 *
 * The live checks at the bottom are the client's alone (CLAUDE.md §10): they read the pearls
 * and the drawn segments, never the answer key.
 */
import {
  BLACK,
  CROSSED,
  LINE,
  NO_PEARL,
  NONE,
  WHITE,
  cellKey,
  type Cell,
  type Edge,
  type EdgeState,
  type Edges,
  type Pearl,
} from "./types";

export function blankEdges(rows: number, cols: number): Edges {
  return {
    h: Array.from({ length: rows }, () => NONE.repeat(Math.max(0, cols - 1))),
    v: Array.from({ length: Math.max(0, rows - 1) }, () => NONE.repeat(cols)),
  };
}

export function edgesFit(edges: Edges, rows: number, cols: number): boolean {
  return (
    edges.h.length === rows &&
    edges.h.every((line) => line.length === cols - 1) &&
    edges.v.length === rows - 1 &&
    edges.v.every((line) => line.length === cols)
  );
}

/** The state of an edge; `null` when it lies off the grid. */
export function edgeState(edges: Edges, { axis, r, c }: Edge): EdgeState | null {
  const char = edges[axis][r]?.[c];
  if (char === undefined || c < 0) return null;
  return char === LINE || char === CROSSED ? char : NONE;
}

export function edgeAt(edges: Edges, edge: Edge): EdgeState {
  return edgeState(edges, edge) ?? NONE;
}

export function withEdge(edges: Edges, { axis, r, c }: Edge, state: EdgeState): Edges {
  const line = edges[axis][r];
  if (line === undefined || c < 0 || c >= line.length || line[c] === state) return edges;
  const lines = [...edges[axis]];
  lines[r] = line.slice(0, c) + state + line.slice(c + 1);
  return { ...edges, [axis]: lines };
}

export type Direction = "L" | "R" | "U" | "D";
export const DIRECTIONS: Direction[] = ["L", "R", "U", "D"];
const STEP: Record<Direction, [number, number]> = { L: [0, -1], R: [0, 1], U: [-1, 0], D: [1, 0] };
const PERPENDICULAR: Record<Direction, Direction[]> = {
  L: ["U", "D"],
  R: ["U", "D"],
  U: ["L", "R"],
  D: ["L", "R"],
};

/** The edge leaving cell (r, c) in a direction, whether or not it lies on the grid. */
export function armOf([r, c]: Cell, direction: Direction): Edge {
  switch (direction) {
    case "L":
      return { axis: "h", r, c: c - 1 };
    case "R":
      return { axis: "h", r, c };
    case "U":
      return { axis: "v", r: r - 1, c };
    case "D":
      return { axis: "v", r, c };
  }
}

export function neighbour([r, c]: Cell, direction: Direction): Cell {
  const [dr, dc] = STEP[direction];
  return [r + dr, c + dc];
}

/** The edges that leave cell (r, c), whichever exist. */
export function edgesAtCell(cell: Cell, edges: Edges): Edge[] {
  return DIRECTIONS.map((d) => armOf(cell, d)).filter((edge) => edgeState(edges, edge) !== null);
}

/** The edge joining two orthogonally adjacent cells, or null. */
export function edgeBetween(a: Cell, b: Cell): Edge | null {
  if (a[0] === b[0] && Math.abs(a[1] - b[1]) === 1)
    return { axis: "h", r: a[0], c: Math.min(a[1], b[1]) };
  if (a[1] === b[1] && Math.abs(a[0] - b[0]) === 1)
    return { axis: "v", r: Math.min(a[0], b[0]), c: a[1] };
  return null;
}

export function endsOf({ axis, r, c }: Edge): [Cell, Cell] {
  return axis === "h"
    ? [
        [r, c],
        [r, c + 1],
      ]
    : [
        [r, c],
        [r + 1, c],
      ];
}

export function pearlAt(pearls: string[], r: number, c: number): Pearl {
  const char = pearls[r]?.[c];
  return char === WHITE || char === BLACK ? char : NO_PEARL;
}

const drawn = (edges: Edges, edge: Edge) => edgeState(edges, edge) === LINE;

/**
 * Whether the loop honours a pearl on cell (r, c). White: straight through, and a turn on at
 * least one of the two cells before and after. Black: a turn, and straight through the next
 * cell on both branches.
 */
export function pearlHolds(pearl: Pearl, edges: Edges, cell: Cell): boolean {
  const on = Object.fromEntries(DIRECTIONS.map((d) => [d, drawn(edges, armOf(cell, d))])) as Record<
    Direction,
    boolean
  >;
  if (DIRECTIONS.filter((d) => on[d]).length !== 2) return false;
  const goesOn = (d: Direction) => drawn(edges, armOf(neighbour(cell, d), d));
  if (pearl === WHITE) {
    if (on.L && on.R) return !(goesOn("L") && goesOn("R"));
    if (on.U && on.D) return !(goesOn("U") && goesOn("D"));
    return false;
  }
  if ((on.L && on.R) || (on.U && on.D)) return false;
  return DIRECTIONS.every((d) => !on[d] || goesOn(d));
}

/** Every pearl the loop misses or breaks, row-major. */
export function pearlViolations(
  pearls: string[],
  edges: Edges,
  rows: number,
  cols: number,
): Cell[] {
  const found: Cell[] = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      const pearl = pearlAt(pearls, r, c);
      if (pearl !== NO_PEARL && !pearlHolds(pearl, edges, [r, c])) found.push([r, c]);
    }
  return found;
}

/** Every edge drawn as a segment of the loop. */
export function segments(edges: Edges): Edge[] {
  const found: Edge[] = [];
  (["h", "v"] as const).forEach((axis) =>
    edges[axis].forEach((line, r) => {
      for (let c = 0; c < line.length; c++) if (line[c] === LINE) found.push({ axis, r, c });
    }),
  );
  return found;
}

export type LoopIssue = "empty" | "branch" | "dead_end" | "several_loops";

interface Piece {
  edges: Edge[];
  degrees: Map<string, number>;
}

/** The segments grouped by connected piece, with each piece's cells and their degrees. */
function pieces(edges: Edges): Piece[] {
  const links = new Map<string, Edge[]>();
  for (const edge of segments(edges))
    for (const cell of endsOf(edge)) {
      const key = cellKey(cell);
      links.set(key, [...(links.get(key) ?? []), edge]);
    }
  const seen = new Set<string>();
  const found: Piece[] = [];
  for (const start of links.keys()) {
    if (seen.has(start)) continue;
    const piece: Piece = { edges: [], degrees: new Map() };
    const counted = new Set<string>();
    const stack = [start];
    seen.add(start);
    while (stack.length > 0) {
      const cell = stack.pop() as string;
      const incident = links.get(cell) ?? [];
      piece.degrees.set(cell, incident.length);
      for (const edge of incident) {
        const id = `${edge.axis}:${edge.r}:${edge.c}`;
        if (!counted.has(id)) {
          counted.add(id);
          piece.edges.push(edge);
        }
        for (const other of endsOf(edge)) {
          const key = cellKey(other);
          if (!seen.has(key)) {
            seen.add(key);
            stack.push(key);
          }
        }
      }
    }
    found.push(piece);
  }
  return found;
}

/** What keeps the segments from being one closed loop; empty when they are (as in Python). */
export function loopIssues(edges: Edges): LoopIssue[] {
  const found = pieces(edges);
  if (found.length === 0) return ["empty"];
  const degrees = found.flatMap((piece) => [...piece.degrees.values()]);
  const issues: LoopIssue[] = [];
  if (degrees.some((d) => d > 2)) issues.push("branch");
  if (degrees.some((d) => d === 1)) issues.push("dead_end");
  if (issues.length > 0) return issues;
  return found.length === 1 ? [] : ["several_loops"];
}

/** Every pearl the loop would honour, as an author places them before removing some. */
export function pearlsOf(edges: Edges, rows: number, cols: number): string[] {
  return Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => {
      if (pearlHolds(WHITE, edges, [r, c])) return WHITE;
      if (pearlHolds(BLACK, edges, [r, c])) return BLACK;
      return NO_PEARL;
    }).join(""),
  );
}

/** Whether the edges are one closed loop honouring every pearl: the verdict, client side. */
export function isSolved(pearls: string[], edges: Edges, rows: number, cols: number): boolean {
  return loopIssues(edges).length === 0 && pearlViolations(pearls, edges, rows, cols).length === 0;
}

// ---- live feedback ------------------------------------------------------------------------

export interface Contradictions {
  /** Pearls whose rule is already broken, or can no longer be met. */
  pearls: Cell[];
  /** Cells where three or four segments meet, or where one segment ends with no way on. */
  cells: Cell[];
  /** Segments of a loop closed while other segments, or pearls, lie outside it. */
  loop: Edge[];
}

/** Whether a pearl can no longer be honoured, whatever the undecided edges become. */
function pearlBroken(pearl: Pearl, edges: Edges, cell: Cell): boolean {
  const state = (edge: Edge) => edgeState(edges, edge);
  const line = (d: Direction) => state(armOf(cell, d)) === LINE;
  const open = (edge: Edge) => {
    const s = state(edge);
    return s === LINE || s === NONE;
  };
  const avail = (d: Direction) => open(armOf(cell, d));
  if (DIRECTIONS.filter(avail).length < 2) return true;
  if (pearl === WHITE) {
    const across = avail("L") && avail("R") && !line("U") && !line("D");
    const along = avail("U") && avail("D") && !line("L") && !line("R");
    if (!across && !along) return true;
    // Straight through, and both neighbours already go straight on: no turn is left.
    const goesOn = (d: Direction) => state(armOf(neighbour(cell, d), d)) === LINE;
    if (line("L") && line("R") && goesOn("L") && goesOn("R")) return true;
    if (line("U") && line("D") && goesOn("U") && goesOn("D")) return true;
    return false;
  }
  // A black branch needs the arm, the next cell's onward edge, and no side exit there.
  const branch = (d: Direction) => {
    const next = neighbour(cell, d);
    return (
      avail(d) &&
      open(armOf(next, d)) &&
      !PERPENDICULAR[d].some((p) => state(armOf(next, p)) === LINE)
    );
  };
  if ((line("L") && line("R")) || (line("U") && line("D"))) return true;
  if (DIRECTIONS.some((d) => line(d) && !branch(d))) return true;
  return !(branch("L") || branch("R")) || !(branch("U") || branch("D"));
}

/**
 * What can no longer lead to a solution, whatever the undecided edges become. Sound, not
 * complete: anything that might still work out is left alone (CLAUDE.md §16.4).
 */
export function contradictions(
  pearls: string[],
  edges: Edges,
  rows: number,
  cols: number,
): Contradictions {
  const cells: Cell[] = [];
  const flaggedPearls: Cell[] = [];
  const pearlCells: string[] = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      const states = edgesAtCell([r, c], edges).map((edge) => edgeAt(edges, edge));
      const degree = states.filter((s) => s === LINE).length;
      const open = states.filter((s) => s === NONE).length;
      if (degree > 2 || (degree === 1 && open === 0)) cells.push([r, c]);
      const pearl = pearlAt(pearls, r, c);
      if (pearl === NO_PEARL) continue;
      pearlCells.push(cellKey([r, c]));
      if (pearlBroken(pearl, edges, [r, c])) flaggedPearls.push([r, c]);
    }

  // A piece whose every cell has two segments is closed: it is the whole loop, or it is wrong.
  const found = pieces(edges);
  const loop = found
    .filter((piece) => [...piece.degrees.values()].every((d) => d === 2))
    .filter((piece) => found.length > 1 || pearlCells.some((key) => !piece.degrees.has(key)))
    .flatMap((piece) => piece.edges);

  return { pearls: flaggedPearls, cells, loop };
}
